<template>
  <v-container
    @click="focus"
    class="calibration_colour"
    data-testid="calibration_colour"
    :data-colour="colour"
    :data-side="side"
  >
    <v-sheet elevation="2" class="calibration_sheet" style="text-align: center">
      <p
        class="calibration_sheet_instructions"
        v-html="$t('calibration_colour.calibration_colour_message', {
          side_from: this.$t(`common.${side}`),
          side_to: opposite(side),
          opposite_colour: this.$t(`common.${opposite_colour}`),
          opposite_colour_css: opposite_colour,
          colour: this.$t(`common.${colour}`)
        })"></p>
      <div class="box">
        <span class="dot" :style="{ 'background-color': dotColour }"></span>
      </div>

      <v-slider
        :id="slider_id"
        ref="CalibrationColourSlider"
        v-model="localValue"
        class="calibration_colour_slider"
        data-testid="calibration_colour_slider"
        :data-colour="colour"
        :data-side="side"
        :min="min_value"
        :max="max_value"
        @change="$emit('change', value)"
      >
        <template v-slot:prepend>
          <v-icon v-if="!phone_screen" @click="decrement">mdi-minus</v-icon>
        </template>

        <template v-slot:append>
          <v-icon v-if="!phone_screen" @click="increment">mdi-plus</v-icon>
        </template>
      </v-slider>
    </v-sheet>
  </v-container>
</template>

<script>

import { mapState } from "vuex";
import {
  MAX_CALIBRATION_BRIGHTNESS,
  MIN_CALIBRATION_BRIGHTNESS,
} from "../lib/calibration_core";

export default {
  props: {
    side: {
      type: String,
      required: true,
    },
    colour: {
      type: String,
      required: true,
    },
    opposite_colour: {
      type: String,
      required: true,
    },
    value: {
      type: Number,
      required: true,
    },
  },
  computed: {
    ...mapState(["phone_screen"]),
    // The slider works in whole percent, storage in a 0-1 fraction. Both ends
    // are driven from the stored range: a value outside it makes the whole
    // calibration unreadable, not just this channel.
    min_value() {
      return MIN_CALIBRATION_BRIGHTNESS * 100;
    },
    max_value() {
      return MAX_CALIBRATION_BRIGHTNESS * 100;
    },
    slider_id() {
      return "calibration_colour_slider-" + this.colour;
    },
    localValue: {
      get() {
        return this.value;
      },
      set(localValue) {
        this.$emit("input", localValue);
      },
    },
    dotColour() {
      let base_colour = parseInt(255 * (this.value / 100));

      if (this.colour == "red") {
        return "rgb(" + base_colour + ",0,0)";
      } else if (this.colour == "blue") {
        return "rgb(0,0," + base_colour + ")";
      } else if (this.colour == "green") {
        return "rgb(0," + base_colour + ",0)";
      } else {
        throw "Invalid colour";
      }
    },
  },
  methods: {
    focus() {
      if (this.$refs["CalibrationColourSlider"].$refs.thumb) {
        this.$refs["CalibrationColourSlider"].$refs.thumb.focus();
      }
    },
    opposite(side) {
      if (side == "left") {
        return this.$t("common.right");
      } else {
        return this.$t("common.left");
      }
    },
    increment() {
      this.localValue = Math.min(this.max_value, this.localValue + 1);
    },
    decrement() {
      this.localValue = Math.max(this.min_value, this.localValue - 1);
    },
  },
};
</script>

<style>
.calibration_colour .box {
  background-color: black;
  text-align: center;
  vertical-align: center;
  height: 260px;
}

.calibration_colour .phone-screen .box {
  height: 100px;
}

.calibration_colour .dot {
  height: 50px;
  width: 50px;
  border-radius: 50%;
  display: inline-block;
  margin-top: 110px;
}

.calibration_colour .phone-screen .dot {
  margin-top: 25px;
}

.calibration_colour .calibration_sheet {
  height: 400px;
  padding: 20px;
}

.calibration_colour .calibration_sheet .calibration_sheet_instructions {
  font-size: 1.1rem;
}

.calibration_colour .phone-screen .calibration_sheet .calibration_sheet_instructions {
  font-size: 1rem;
}

.calibration_colour .phone-screen .calibration_sheet {
  height: 320px;
  padding: 10px;
  font-size: smaller;
}

.calibration_colour .calibration_colour_slider {
  margin-bottom: 20px;
  margin-top: 20px;
}

/* iPad in landscape */
@media only screen and (min-device-width: 740px) and (max-device-width: 1080px) and (orientation: landscape) {
  .calibration_colour .box {
    height: 180px;
  }

  .calibration_colour .dot {
    margin-top: 70px;
  }

  .calibration_colour .calibration_sheet {
    height: 350px;
  }
}

.phone-screen .calibration_colour .box {
  height: 100px;
}

.phone-screen .calibration_colour .dot {
  margin-top: 25px;
}

.phone-screen .calibration_colour .calibration_sheet {
  height: 350px;
  padding: 10px;
}
</style>
